import { inject, Injectable, NgZone } from "@angular/core";
import { STORE_NAME_PROJECT_IMAGES } from "@app/indexed-db-config";
import { ProjectApiService } from "@domain/project/api/project-api.service";
import { Floorplan } from "@domain/project/floorplan/floorplan";
import { ProjectEventType } from "@domain/project/project-event";
import { ScopedProject } from "@domain/project/project-guards";
import { TemplateService } from "@domain/project/template/template.service";
import { BusinessScope } from "@lib/constants/business-scope";
import { ErrorReportingService } from "@utils/error-reporting.service";
import { ImageConverterService } from "@utils/image-converter.service";
import { instanceToPlain } from "class-transformer";
import { NgxIndexedDBService } from "ngx-indexed-db";
import { catchError, forkJoin, map, Observable, of, switchMap, tap } from "rxjs";
import { v4 as uuidv4 } from "uuid";
import { ProjectImageMetadata } from "./project-image-metadata";

export enum SyncState {
  NEW = "NEW",
  UPDATED = "UPDATED",
  DELETED = "DELETED",
  SYNCED = "SYNCED",
}

export interface ProjectImageData {
  blob?: Blob;
  previewBlob?: Blob;
  imageMetadata: ProjectImageMetadata;
  projectId?: string;
  syncState: SyncState;
}

@Injectable({
  providedIn: "root",
})
export class ProjectImageService {
  private templateService: TemplateService = inject(TemplateService);
  private imageConverterService = inject(ImageConverterService);
  private dbService = inject(NgxIndexedDBService);
  private projectApiService = inject(ProjectApiService);
  private ngZone = inject(NgZone);
  private errorReportingService = inject(ErrorReportingService);

  private readonly MAX_PREVIEW_WIDTH = 300;
  private readonly MAX_PREVIEW_HEIGHT = 300;
  private readonly PREVIEW_QUALITY = 0.7;

  private readonly WHITELISTED_IMAGE_TYPES: string[] = [
    "image/png",
    "image/jpg",
    "image/jpeg",
    "image/gif",
    "image/svg",
    "image/svg+xml",
    "image/webp",
    "image/heic",
    "image/heif",
  ];

  getProjectImageTags(scope: BusinessScope) {
    return this.templateService.getGlobalTemplate(scope).pipe(
      map((template) => {
        return template!.properties!.project!.images!.tags ?? [];
      }),
    );
  }

  convertHeicImages(files: File[]): Observable<File[]> {
    return forkJoin(
      [...files].map((file) => {
        return /\.(heic|heif)$/i.test(file.name) ? this.imageConverterService.convertHeicToWebP(file) : of(file);
      }),
    );
  }

  checkIfTypeIsWhitelisted(files: FileList) {
    const fileArray = Array.from(files);
    const unsupportedFiles: File[] = [];
    fileArray.forEach((file) => {
      if (!this.isFileTypeOnWhiteList(file)) {
        unsupportedFiles.push(file);
      }
    });
    return unsupportedFiles;
  }

  filterUnsupportedFileTypes(unsupportedFiles: File[]) {
    let fileNames: string = "";
    unsupportedFiles.forEach((file, index) => {
      if (unsupportedFiles.length - index === 1) {
        fileNames += `${file.name} `;
      } else {
        fileNames += `${file.name}, `;
      }
    });
    return fileNames.trim();
  }

  createPreviewImage(file: Blob): Observable<Blob> {
    return new Observable((observer) => {
      const img = new Image();
      const reader = new FileReader();

      reader.onload = (e) => {
        img.src = e.target?.result as string;
      };

      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d")!;

        let width = img.width;
        let height = img.height;

        if (width > this.MAX_PREVIEW_WIDTH || height > this.MAX_PREVIEW_HEIGHT) {
          const ratio = Math.min(this.MAX_PREVIEW_WIDTH / width, this.MAX_PREVIEW_HEIGHT / height);
          width = width * ratio;
          height = height * ratio;
        }

        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            this.ngZone.run(() => {
              if (blob) {
                observer.next(blob);
                observer.complete();
              } else {
                observer.error(new Error("Preview generation failed"));
              }
            });
          },
          "image/webp",
          this.PREVIEW_QUALITY,
        );
      };

      img.onerror = () => {
        observer.error(new Error("Image could not be loaded"));
      };

      reader.readAsDataURL(file);
    });
  }

  saveImagesToIndexedDB(imageData: ProjectImageData[], project?: ScopedProject) {
    const dataToStore = imageData.map((image) => {
      if (!image.previewBlob || !image.imageMetadata || !image.projectId) {
        throw new Error("Missing required image data");
      }
      return {
        id: image.imageMetadata.id,
        blob: image.blob,
        previewBlob: image.previewBlob,
        imageMetadata: instanceToPlain(image.imageMetadata),
        projectId: image.projectId,
        syncState: image.syncState,
      };
    });
    return this.dbService.bulkPut(STORE_NAME_PROJECT_IMAGES, dataToStore).pipe(
      tap(() =>
        project?.publishUpdate(
          ProjectEventType.IMAGE_ADDED,
          imageData.map((image) => image.imageMetadata),
        ),
      ),
    );
  }

  getImagesFromIndexedDB(projectId: string): Observable<ProjectImageData[]> {
    return this.dbService.getAll<any>(STORE_NAME_PROJECT_IMAGES).pipe(
      map((items) => items.filter((item) => item.projectId === projectId)),
      map((items) =>
        items.map((item) => ({
          blob: item.blob,
          previewBlob: item.previewBlob,
          imageMetadata: new ProjectImageMetadata(
            item.imageMetadata.id,
            item.imageMetadata._name || item.imageMetadata.name,
            new Date(item.imageMetadata.addDate),
            item.imageMetadata.description,
            item.imageMetadata.tags,
          ),
          projectId: item.projectId,
          syncState: item.syncState,
        })),
      ),
    );
  }

  copyImages(oldProjectId: string, newProject: ScopedProject, cloudId?: string): Observable<Map<string, string>> {
    return this.getImagesFromIndexedDB(oldProjectId).pipe(
      switchMap((imageData) => {
        const imageIdMap = new Map<string, string>();
        const candidates = imageData.filter((image) => image.syncState !== SyncState.DELETED);

        if (candidates.length === 0) {
          return of(imageIdMap);
        }

        const prepared$ = candidates.map((image) => {
          if (image.blob) {
            return of(image);
          }

          if (!cloudId) {
            this.errorReportingService.captureException(
              new Error("Skipping image copy: no blob and no cloud id to reload the original from"),
              { tags: { area: "image-sync" }, extra: { projectId: oldProjectId, imageId: image.imageMetadata.id } },
            );
            return of(undefined);
          }

          return this.loadImage(cloudId, image.imageMetadata.id).pipe(
            map((blob) => ({ ...image, blob })),
            catchError((err) => {
              this.errorReportingService.captureException(
                err instanceof Error ? err : new Error("Failed to reload image for copy"),
                {
                  tags: { area: "image-sync" },
                  extra: { projectId: oldProjectId, imageId: image.imageMetadata.id },
                },
              );
              return of(undefined);
            }),
          );
        });

        return forkJoin(prepared$).pipe(
          switchMap((prepared) => {
            const copiedData = prepared
              .filter((image): image is ProjectImageData => !!image)
              .map((image) => {
                const newId = uuidv4();
                imageIdMap.set(image.imageMetadata.id, newId);
                const plainImageMetadata = {
                  ...instanceToPlain(image.imageMetadata),
                  id: newId,
                };
                return {
                  id: newId,
                  blob: image.blob,
                  previewBlob: image.previewBlob,
                  imageMetadata: plainImageMetadata,
                  projectId: newProject.id,
                  syncState: SyncState.NEW,
                };
              });

            if (copiedData.length === 0) {
              return of(imageIdMap);
            }

            return this.dbService.bulkPut(STORE_NAME_PROJECT_IMAGES, copiedData).pipe(map(() => imageIdMap));
          }),
        );
      }),
    );
  }

  getPreviewImageFromIndexedDB(imageId: string) {
    return this.dbService.getByKey<any>(STORE_NAME_PROJECT_IMAGES, imageId).pipe(
      map((item) => {
        if (!item) {
          console.error(`No preview image data found for id ${imageId}`);
          return undefined;
        }
        return item?.previewBlob;
      }),
    );
  }

  getImageSyncStatus(imageId: string) {
    return this.dbService.getByKey<any>(STORE_NAME_PROJECT_IMAGES, imageId).pipe(
      map((item) => {
        if (!item) {
          console.error(`No image data found for id ${imageId}`);
          return undefined;
        }
        return item?.syncState;
      }),
    );
  }

  getOriginalImageFromIndexedDB(imageId: string) {
    return this.dbService.getByKey<any>(STORE_NAME_PROJECT_IMAGES, imageId).pipe(
      map((item) => {
        if (!item) {
          console.error(`No original image data found for id ${imageId}`);
          return undefined;
        }
        return item?.blob ?? item?.previewBlob;
      }),
    );
  }

  getUnsyncedImages(projectId: string): Observable<ProjectImageData[]> {
    return this.dbService.getAll<any>(STORE_NAME_PROJECT_IMAGES).pipe(
      map((items) => items.filter((item) => item.syncState && item.syncState !== SyncState.SYNCED)),
      map((items) => items.filter((item) => item.projectId === projectId)),
      map((items) =>
        items.map((item) => ({
          blob: item.blob,
          previewBlob: item.previewBlob,
          imageMetadata: new ProjectImageMetadata(
            item.imageMetadata.id,
            item.imageMetadata._name || item.imageMetadata.name,
            new Date(item.imageMetadata.addDate),
            item.imageMetadata.description,
            item.imageMetadata.tags,
          ),
          projectId: item.projectId,
          syncState: item.syncState,
        })),
      ),
      catchError((err) => {
        console.error("getUnsyncedImages failed", err);
        return of([]);
      }),
    );
  }

  getImageMetadataFromIndexedDB(imageId: string): Observable<ProjectImageMetadata | undefined> {
    return this.dbService.getByKey<any>(STORE_NAME_PROJECT_IMAGES, imageId).pipe(
      map((item) => {
        if (!item) {
          console.error(`getImageMetadataFromIndexedDB: no entry found for imageId=${imageId}`);
          return undefined;
        }
        return new ProjectImageMetadata(
          item.imageMetadata.id,
          item.imageMetadata._name || item.imageMetadata.name,
          new Date(item.imageMetadata.addDate),
          item.imageMetadata.description,
          item.imageMetadata.tags,
        );
      }),
      catchError((err) => {
        console.error(`getImageMetadataFromIndexedDB: error for imageId=${imageId}`, err);
        return of(undefined);
      }),
    );
  }

  getAllImageMetadataFromIndexedDB(projectId: string): Observable<ProjectImageMetadata[]> {
    return this.dbService.getAll<any>(STORE_NAME_PROJECT_IMAGES).pipe(
      map((items) => items.filter((item) => item.projectId === projectId).filter((item) => item.syncState !== SyncState.DELETED)),
      map((items) =>
        items.map(
          (item) =>
            new ProjectImageMetadata(
              item.imageMetadata.id,
              item.imageMetadata._name || item.imageMetadata.name,
              new Date(item.imageMetadata.addDate),
              item.imageMetadata.description,
              item.imageMetadata.tags,
            ),
        ),
      ),
    );
  }

  markImageAsSynced(imageId: string): Observable<void> {
    return this.dbService.getByKey<any>(STORE_NAME_PROJECT_IMAGES, imageId).pipe(
      switchMap((entry) => {
        if (!entry) {
          console.error(`No image found for imageId=${imageId}`);
          return of(void 0);
        }
        const updated = { ...entry, syncState: SyncState.SYNCED };
        delete updated.blob; // delete original full sized blob from indexedDB
        return this.dbService.update(STORE_NAME_PROJECT_IMAGES, updated).pipe(map(() => void 0));
      }),
    );
  }

  loadImage(cloudId: string, imageId: string) {
    return this.projectApiService.loadProjectImage(cloudId, imageId);
  }

  getProjectImages(cloudId: string, projectId: string) {
    return this.projectApiService.loadProjectImages(cloudId).pipe(
      switchMap((images) => {
        if (images.length === 0) return of([]);

        return this.getImagesFromIndexedDB(projectId).pipe(
          switchMap((cachedImages) => {
            const cachedMap = new Map(cachedImages.map((img) => [img.imageMetadata.id, img]));

            const missingImages = images.filter((img) => !cachedMap.has(img.id));

            if (missingImages.length === 0) {
              return of(cachedImages);
            }

            return forkJoin(
              missingImages.map((image) =>
                this.getImagePreview(cloudId, image.id).pipe(
                  map((blob) => ({
                    previewBlob: blob,
                    imageMetadata: image,
                    projectId: projectId,
                    syncState: SyncState.SYNCED,
                  })),
                ),
              ),
            ).pipe(
              switchMap((newImages) => this.saveImagesToIndexedDB(newImages).pipe(map(() => [...cachedImages, ...newImages]))),
            );
          }),
        );
      }),
    );
  }

  /**
   * Resets every image record of the project to the cloud state: cloud images are rewritten as SYNCED
   * (reusing an already cached preview blob), local-only images are hard-deleted. Used when the user
   * discards the local copy in a version conflict - without this, the next sync cycle pushes the discarded
   * local changes into the adopted cloud project and even deletes a locally removed image from the cloud.
   */
  replaceImagesWithCloudVersion(cloudId: string, projectId: string): Observable<void> {
    return forkJoin([this.projectApiService.loadProjectImages(cloudId), this.getImagesFromIndexedDB(projectId)]).pipe(
      switchMap(([cloudImages, cachedImages]) => {
        const cachedById = new Map(cachedImages.map((image) => [image.imageMetadata.id, image]));
        const cloudIds = new Set<string>(cloudImages.map((cloudImage) => cloudImage.id));
        const obsoleteIds = cachedImages.map((image) => image.imageMetadata.id).filter((id) => !cloudIds.has(id));

        const cloudRecords = cloudImages.map((cloudImage) => {
          const cachedPreview = cachedById.get(cloudImage.id)?.previewBlob;
          const preview = cachedPreview ? of(cachedPreview) : this.getImagePreview(cloudId, cloudImage.id);
          return preview.pipe(
            map((previewBlob) => ({
              previewBlob,
              // The full sized blob is intentionally omitted: synced images do not keep one either,
              // the original is loaded on demand via loadImage().
              imageMetadata: cloudImage,
              projectId,
              syncState: SyncState.SYNCED,
            })),
          );
        });

        return this.storeCloudImagesAsSynced(cloudRecords).pipe(switchMap(() => this.deleteImagesByIds(obsoleteIds)));
      }),
    );
  }

  /**
   * Prepares the image records of the project so that the local state wins over the cloud state: every
   * already SYNCED record is flagged as UPDATED and every cloud image without a local counterpart is written
   * as a DELETED tombstone. Used when the user overwrites the cloud version in a version conflict.
   *
   * Without it, the local image metadata is never pushed - so the cloud keeps the other user's names, tags
   * and descriptions - and a cloud only image is downloaded back into the local project on the next open.
   */
  stageLocalImagesForCloudOverwrite(cloudId: string, projectId: string): Observable<void> {
    return forkJoin([
      this.projectApiService.loadProjectImages(cloudId),
      this.dbService.getAll<any>(STORE_NAME_PROJECT_IMAGES),
    ]).pipe(
      switchMap(([cloudImages, storedItems]) => {
        const localItems = storedItems.filter((item) => item.projectId === projectId);
        const localIds = new Set<string>(localItems.map((item) => item.id));

        // Records that already carry local changes (NEW / UPDATED / DELETED) are dirty already and are
        // uploaded as they are.
        const imagesToReupload = localItems
          .filter((item) => item.syncState === SyncState.SYNCED)
          .map((item) => ({ ...item, syncState: SyncState.UPDATED }));

        // An image that only exists in the cloud is part of the version the user decided to discard.
        // The tombstone hides it locally and makes the next sync remove it from the cloud.
        const cloudOnlyTombstones = cloudImages
          .filter((cloudImage) => !localIds.has(cloudImage.id))
          .map((cloudImage) => ({
            id: cloudImage.id,
            imageMetadata: cloudImage,
            projectId,
            syncState: SyncState.DELETED,
          }));

        const dataToStore = [...imagesToReupload, ...cloudOnlyTombstones];
        if (dataToStore.length === 0) {
          return of(void 0);
        }

        return this.dbService.bulkPut(STORE_NAME_PROJECT_IMAGES, dataToStore).pipe(map(() => void 0));
      }),
    );
  }

  private storeCloudImagesAsSynced(cloudRecords: Observable<ProjectImageData>[]): Observable<void> {
    if (cloudRecords.length === 0) {
      return of(void 0);
    }
    return forkJoin(cloudRecords).pipe(switchMap((images) => this.saveImagesToIndexedDB(images).pipe(map(() => void 0))));
  }

  private deleteImagesByIds(imageIds: string[]): Observable<void> {
    if (imageIds.length === 0) {
      return of(void 0);
    }
    const deleteObservables = imageIds.map((imageId) => this.dbService.deleteByKey(STORE_NAME_PROJECT_IMAGES, imageId));
    return forkJoin(deleteObservables).pipe(map(() => void 0));
  }

  removeImage(imageId: string) {
    return this.dbService.getByKey<any>(STORE_NAME_PROJECT_IMAGES, imageId).pipe(
      switchMap((entry) => {
        if (!entry) {
          console.error(`No image found for imageId=${imageId}`);
          return of(void 0);
        }
        const updated = { ...entry, syncState: SyncState.DELETED };
        return this.dbService.update(STORE_NAME_PROJECT_IMAGES, updated).pipe(map(() => void 0));
      }),
    );
  }

  deleteImageFromFloorplans(project: ScopedProject, image: ProjectImageMetadata) {
    project.cachedFloorplans.forEach((floorplan: Floorplan) => floorplan.deleteImagesByProjectImage(image));
    project.publishUpdate(ProjectEventType.IMAGE_DELETED, image);
    project.setImagePositionIds();
  }

  removeImageFromIndexedDB(imageId: string) {
    if (!imageId) {
      console.error(`No image found for imageId=${imageId} while trying to remove`);
      return of(void 0);
    }
    return this.dbService.deleteByKey(STORE_NAME_PROJECT_IMAGES, imageId);
  }

  /**
   * Updates the metadata of an image. `project` must be passed for every user driven change - see
   * {@link saveImagesToIndexedDB} for why.
   */
  updateImageInIndexedDB(image: ProjectImageMetadata, project?: ScopedProject) {
    return this.dbService.getByKey<any>(STORE_NAME_PROJECT_IMAGES, image.id).pipe(
      switchMap((entry) => {
        if (!entry) {
          console.error(`No image found for imageId=${image.id} while trying to update`);
          return of(void 0);
        }
        const syncState = entry.syncState === SyncState.NEW ? SyncState.NEW : SyncState.UPDATED;
        const updated = { ...entry, imageMetadata: instanceToPlain(image), syncState: syncState };
        return this.dbService.update(STORE_NAME_PROJECT_IMAGES, updated).pipe(
          tap(() => project?.publishUpdate(ProjectEventType.IMAGE_UPDATED, image)),
          map(() => void 0),
        );
      }),
    );
  }

  deleteProjectImagesFromIndexedDB(projectId: string) {
    return this.dbService.getAll<any>(STORE_NAME_PROJECT_IMAGES).pipe(
      map((items) => items.filter((item) => item.projectId === projectId)),
      switchMap((items) => {
        if (items.length === 0) {
          return of(void 0);
        }
        const deleteObservables = items.map((item) => this.dbService.deleteByKey(STORE_NAME_PROJECT_IMAGES, item.id));
        return forkJoin(deleteObservables).pipe(map(() => void 0));
      }),
    );
  }

  /** Emits true only when the API confirmed every image. Successful entries are marked as synced either way. */
  addImages(images: ProjectImageData[], cloudId?: string): Observable<boolean> {
    if (!cloudId) {
      throw new Error("cloudId is undefined");
    }

    return this.projectApiService.addImages(cloudId, images).pipe(
      switchMap((response: any[]) => {
        const entries = response ?? [];
        const allSucceeded = entries.length === images.length && entries.every((res) => res?.success);
        const syncCalls = entries.filter((res) => res?.success).map((res) => this.markImageAsSynced(res.imageId));

        if (syncCalls.length === 0) {
          return of(allSucceeded);
        }

        return forkJoin(syncCalls).pipe(map(() => allSucceeded));
      }),
    );
  }

  /** Emits true only when the API confirmed every metadata update. */
  updateImages(images: ProjectImageData[], cloudId: string): Observable<boolean> {
    const imageMetadata: ProjectImageMetadata[] = images.map((img) => img.imageMetadata);

    if (imageMetadata.length === 0) {
      return of(true);
    }

    return this.projectApiService.updateProjectImages(cloudId, imageMetadata).pipe(
      switchMap((response: any[]) => {
        const entries = response ?? [];
        const allSucceeded = entries.length === imageMetadata.length && entries.every((res) => res?.success);
        const syncCalls = entries.filter((res) => res?.success).map((res) => this.markImageAsSynced(res.imageId));

        if (syncCalls.length === 0) {
          return of(allSucceeded);
        }

        return forkJoin(syncCalls).pipe(map(() => allSucceeded));
      }),
    );
  }

  /** Emits true only when the API confirmed every deletion. */
  removeImages(images: ProjectImageData[], cloudId: string): Observable<boolean> {
    const ids = images.map((img) => img.imageMetadata.id).filter((id): id is string => !!id);

    if (ids.length === 0) {
      return of(true);
    }

    return this.projectApiService.deleteProjectImages(cloudId, ids).pipe(
      switchMap((response: any[]) => {
        const entries = response ?? [];
        const allSucceeded = entries.length === ids.length && entries.every((res) => res?.success);
        const syncCalls = entries.map((res) => this.removeImageFromIndexedDB(res.imageId));

        if (syncCalls.length === 0) {
          return of(allSucceeded);
        }

        return forkJoin(syncCalls).pipe(map(() => allSucceeded));
      }),
    );
  }

  private isFileTypeOnWhiteList(file: File): boolean {
    if (this.WHITELISTED_IMAGE_TYPES.includes(file.type)) {
      return true;
    }
    // Mime type can be empty for heic files uploaded on a windows device, so as a fallback the file extension is checked
    if (!file.type && /\.(heic|heif)$/i.test(file.name)) {
      return true;
    }
    return false;
  }

  private getImagePreview(cloudId: string, imageId: string): Observable<Blob> {
    return this.projectApiService.loadPreviewImage(cloudId, imageId);
  }
}
